import { isDocxFileName } from '@omnea-app/packages/universal/ocrApiClient/ocrFileExtensions'
import type { SuggestedEditsProps, SuggestedEditsStepStatus } from '../types'
import type { ReviewStatus } from '@omnea-app/packages/universal/form/elements/files'

// ~4 polls of headroom over the observed 1-3s claim gap.
export const SUGGESTED_EDITS_DISPATCH_BUDGET_MS = 20_000

// Matches the backend's own drafting gate: OOXML only, so a legacy binary `.doc` is out despite
// deriving as WORD for review and text extraction.
export const isSuggestedEditsEligibleFile = (fileName: string): boolean => isDocxFileName(fileName)

type HasDraftedInput = {
  fileName: string
  suggestedEditsStatus: SuggestedEditsStepStatus | null
}

/** Drafting's silence about a rule only means something once it ran to completion. */
export const hasDraftedSuggestedEdits = ({ fileName, suggestedEditsStatus }: HasDraftedInput): boolean =>
  isSuggestedEditsEligibleFile(fileName) && suggestedEditsStatus === 'completed'

type CanMakeOwnEditsInput = {
  fileName: string
  suggestedEditsStatus: SuggestedEditsStepStatus | null
  reviewStatus: ReviewStatus | undefined
  hasDraftedResult: boolean
  hasFailingFinding: boolean
}

export const canMakeOwnEdits = ({
  fileName,
  suggestedEditsStatus,
  reviewStatus,
  hasDraftedResult,
  hasFailingFinding,
}: CanMakeOwnEditsInput): boolean =>
  isSuggestedEditsEligibleFile(fileName) &&
  ((suggestedEditsStatus === 'completed' && hasDraftedResult) ||
    (suggestedEditsStatus === null && reviewStatus !== undefined && !hasFailingFinding))

export type SuggestedEditsNoticeState = 'hidden' | 'ineligibleFile' | 'noFailingChecks' | 'loading' | 'failed' | 'ready'

type ResolveNoticeStateInput = {
  isEligibleFile: boolean
  suggestedEditsStatus: SuggestedEditsStepStatus | null
  /** The condition the backend dispatches on, so it is also why drafting never started. */
  hasFailingCheck: boolean
}

/**
 * Silent where nothing failed: there are no edits to look for, so the format never matters. A null
 * status with a failing check is the window before dispatch is claimed, which the poll is still
 * chasing — calling that a refusal would be wrong.
 */
export const resolveSuggestedEditsNoticeState = ({
  isEligibleFile,
  suggestedEditsStatus,
  hasFailingCheck,
}: ResolveNoticeStateInput): SuggestedEditsNoticeState => {
  if (!isEligibleFile) return hasFailingCheck ? 'ineligibleFile' : 'hidden'
  if (suggestedEditsStatus === null) return hasFailingCheck ? 'hidden' : 'noFailingChecks'
  if (suggestedEditsStatus === 'pending') return 'loading'
  if (suggestedEditsStatus === 'failed') return 'failed'
  return 'ready'
}

type IsAwaitingSuggestedEditsDispatchInput = {
  suggestedEditsStatus: SuggestedEditsStepStatus | null
  hasFailingFinding: boolean
  terminalSince: number | null
  now: number
}

// A null status also covers the terminal-but-not-yet-claimed window, not just "never dispatched".
// Gates on the exact `'fail'` status, matching the backend's own dispatch condition — not
// `countFailingFindings`, which also counts `not_found`/`error` and would poll for reviews that never dispatch.
export const isAwaitingSuggestedEditsDispatch = ({
  suggestedEditsStatus,
  hasFailingFinding,
  terminalSince,
  now,
}: IsAwaitingSuggestedEditsDispatchInput): boolean => {
  if (suggestedEditsStatus !== null || !hasFailingFinding || terminalSince === null) return false
  // A clock behind the server's makes `elapsed` negative, which would read as "not yet expired" forever.
  const elapsed = now - terminalSince
  return elapsed >= 0 && elapsed < SUGGESTED_EDITS_DISPATCH_BUDGET_MS
}

export type DocumentPrepareState = 'idle' | 'preparing' | 'ready' | 'failed'

type ResolveDocumentPrepareStateInput = {
  suggestedEditsDocumentStatus: SuggestedEditsStepStatus | null
}

/**
 * Only meaningful once `resolveSuggestedEditsNoticeState` returns 'ready'. A null status is 'idle'
 * — nobody has asked for a revision yet — so no elapsed-time check is involved: the server reports an
 * abandoned `'pending'` as `'failed'` on every read.
 */
export const resolveDocumentPrepareState = ({
  suggestedEditsDocumentStatus,
}: ResolveDocumentPrepareStateInput): DocumentPrepareState => {
  if (suggestedEditsDocumentStatus === null) return 'idle'
  if (suggestedEditsDocumentStatus === 'completed') return 'ready'
  if (suggestedEditsDocumentStatus === 'failed') return 'failed'
  return 'preparing'
}

export const isAppliedRevisionHead = ({
  acceptTarget,
  revisedFileStoreId,
  appliedFileStoreIds,
}: Pick<SuggestedEditsProps, 'acceptTarget' | 'revisedFileStoreId' | 'appliedFileStoreIds'>): boolean =>
  acceptTarget !== undefined &&
  (acceptTarget.headFilestoreId === revisedFileStoreId || appliedFileStoreIds.includes(acceptTarget.headFilestoreId))
